package one.healthium.settings.ui.health_kit

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.ButtonColors
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import loco.HEALTH_KIT_CONNECTION_TITLE_FORMAT
import loco.HEALTH_KIT_CONNECT_BUTTON
import loco.HEALTH_KIT_MANAGE_PERMISSIONS_BUTTON
import loco.HEALTH_KIT_SYNC_DESCRIPTION
import one.healthium.base.launcher.LocalPlatformLauncher
import one.healthium.base.preferences.AppPreferences
import one.healthium.base.utils.BundleUtils
import one.healthium.healthKit.data.HealthKitHelper
import one.healthium.healthKit.data.HealthKitStatus
import one.healthium.healthKit.ui.ConnectBannerHelper
import one.healthium.healthKit.ui.ConnectFlowAction
import one.healthium.healthKit.ui.ConnectFlowContainer
import one.healthium.loco.LString
import one.healthium.loco.resolve
import one.healthium.ui.compose.components.AppBackNavigationButton
import one.healthium.ui.compose.components.AppButton
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.components.AppTopBar
import one.healthium.ui.compose.modifier.adaptiveWidthTwoPane
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.black75
import one.healthium.ui.compose.theme.white
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject

interface HealthKitSettingsEventDelegate {
    fun onBackClicked()
}

@Composable
fun HealthKitSettingsScreen(
    eventDelegate: HealthKitSettingsEventDelegate,
) {
    val healthKitHelper = koinInject<HealthKitHelper>()
    val state by healthKitHelper.healthKitStatus.collectAsState()

    HealthKitSettingsScreenContent(
        state = state,
        onBackClicked = eventDelegate::onBackClicked,
    )

}

@Composable
internal fun HealthKitSettingsScreenContent(
    state: HealthKitStatus,
    onBackClicked: () -> Unit,
) {
    val appPreferences = koinInject<AppPreferences>()
    // Introduced LaunchedEffect to call recomposition when state changes
    LaunchedEffect(state) {}
    AppScaffold(
        topBar = {
            AppTopBar(
                title = {
                    Text(ConnectBannerHelper.healthKitName.resolve())
                },
                navigationIcon = { AppBackNavigationButton(onClick = onBackClicked) },
            )
        }
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(it)
                .padding(top = 24.dp)
        ) {
            if (appPreferences.didReactToHKBanner == true) {
                HealthKitHint(Modifier.adaptiveWidthTwoPane().align(Alignment.TopCenter))
            } else {
                HealthKitConnectBanner(Modifier.adaptiveWidthTwoPane().align(Alignment.TopCenter))
            }
        }
    }
}

@Composable
private fun HealthKitHint(
    modifier: Modifier = Modifier,
    icon: DrawableResource = ConnectBannerHelper.healthKitIcon,
    kitName: LString = ConnectBannerHelper.healthKitName,
) {
    val platformLauncher = LocalPlatformLauncher.current

    Surface(
        modifier = modifier
            .padding(horizontal = 12.dp)
            .fillMaxWidth(),
        color = AppTheme.colors.primary,
        shape = AppTheme.shapes.large,
    ) {

        Column(
            modifier = Modifier
                .padding(top = 24.dp)
                .fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {

            Image(
                painter = painterResource(icon),
                contentDescription = null,
                modifier = Modifier.size(40.dp),
            )
            Spacer(modifier = Modifier.height(12.dp))

            Text(
                text = stringResource(loco.Res.string.HEALTH_KIT_CONNECTION_TITLE_FORMAT, kitName.resolve()),
                style = AppTheme.textStyles.secondary_17_500,
                color = AppTheme.colors.textInverted100,
            )

            Spacer(modifier = Modifier.height(12.dp))

            Text(
                modifier = Modifier
                    .padding(horizontal = 24.dp),
                text = ConnectBannerHelper.healthKitManageHint(BundleUtils.getAppName(platformLauncher)).resolve(),
                style = AppTheme.textStyles.secondary_14_400,
                color = AppTheme.colors.textInverted100,
                textAlign = TextAlign.Center,
            )

            ConnectFlowContainer(action = ConnectFlowAction.MANAGE) {
                AppButton(
                    onClick = { it.invoke() },
                    modifier = Modifier
                        .padding(top = 33.dp, bottom = 24.dp)
                        .height(45.dp),
                    colors = ButtonColors(
                        containerColor = black75,
                        contentColor = white,
                        disabledContentColor = white,
                        disabledContainerColor = black75
                    )
                ) {
                    Text(stringResource(loco.Res.string.HEALTH_KIT_MANAGE_PERMISSIONS_BUTTON))
                }
            }
        }
    }
}

@Composable
private fun HealthKitConnectBanner(
    modifier: Modifier = Modifier,
    icon: DrawableResource = ConnectBannerHelper.healthKitIcon,
    kitName: LString = ConnectBannerHelper.healthKitName,
) {

    Surface(
        modifier = modifier
            .padding(horizontal = 12.dp)
            .fillMaxWidth(),
        color = AppTheme.colors.primary,
        shape = AppTheme.shapes.large,
    ) {

        Column(
            modifier = Modifier
                .padding(top = 24.dp)
                .fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {

            Image(
                painter = painterResource(icon),
                contentDescription = null,
                modifier = Modifier.size(40.dp),
            )
            Spacer(modifier = Modifier.height(12.dp))

            Text(
                text = stringResource(loco.Res.string.HEALTH_KIT_CONNECTION_TITLE_FORMAT, kitName.resolve()),
                style = AppTheme.textStyles.secondary_17_500,
                color = AppTheme.colors.textInverted100,
            )

            Spacer(modifier = Modifier.height(12.dp))

            Text(
                modifier = Modifier.padding(horizontal = 24.dp),
                text = stringResource(loco.Res.string.HEALTH_KIT_SYNC_DESCRIPTION),
                style = AppTheme.textStyles.secondary_14_400,
                color = AppTheme.colors.textInverted100,
                textAlign = TextAlign.Center,
            )
            ConnectFlowContainer(action = ConnectFlowAction.CONNECT) {
                AppButton(
                    onClick = { it.invoke() },
                    modifier = Modifier
                        .padding(top = 33.dp, bottom = 24.dp)
                        .height(45.dp),
                    colors = ButtonColors(
                        containerColor = black75,
                        contentColor = white,
                        disabledContentColor = white,
                        disabledContainerColor = black75
                    )
                ) {
                    Text(stringResource(loco.Res.string.HEALTH_KIT_CONNECT_BUTTON))
                }
            }
        }
    }
}